/**
 * Code action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/document";

/**
 * Hook to get the setCode action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 * This action sets the user code (behavior) for the document.
 *
 * @returns A function to set the document code
 */
export function useSetCode(): (code: string, hasErrors: boolean) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (code: string, hasErrors: boolean) => {
            documentStore.getState().setSimulationCode(code, hasErrors);
        },
        [documentStore],
    );
}

/**
 * Factory function to create code actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch code actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @returns An object with code action methods
 */
export function createCodeActions(documentStore: DocumentStore) {
    return {
        setCode: (code: string, hasErrors: boolean) => {
            documentStore.getState().setSimulationCode(code, hasErrors);
        },
    };
}
